<template>
  <div class="other-details-tab">
    <MessageContainer
      :messages="otherDetailsMessages"
      message-class="rygen-message"
    />

    <div class="other-details-columns">
      <div class="p-data-table-carrier-details w-6">
        <h2 class="section-subheader">
          {{
            t('common.paramDetails', {
              param: t('carriers.carrierCompliance'),
            })
          }}
        </h2>

        <DataTable
          :value="collectRateQuoteCarrierDetails(rateQuote)"
          class="rate-detail-table p-datatable-sm datatable-no-header"
          auto-layout
        >
          <Column field="k" header-style="display:none;" />
          <Column field="v" header-style="display:none;" />
        </DataTable>
      </div>

      <div class="quote-details w-6">
        <InsuranceInformation :rate-quote="rateQuote" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, h, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { lowerCase, renderDateTime, titleCase } from '@rygen/rygen-ui/i18n';

import { useDoNotDispatch } from '@/composables';
import { MessageSpecification, RateQuote } from '@/model';

import InsuranceInformation from '@/components/rateShop/expandedDetails/InsuranceInformation.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
});
const { rateQuote } = toRefs(props);

const { t } = useI18n();
const { canOverrideCarrierDoNotDispatch, isDoNotDispatchEnabled } =
  useDoNotDispatch();

const expiredMessage = computed((): MessageSpecification => {
  return {
    id: 'expired-error',
    text: t('rateShop.rateQuoteExpired', {
      date: renderDateTime(rateQuote.value.quoteExpiration),
    }),
    severity: 'error',
    visible:
      !!rateQuote.value.quoteExpiration &&
      rateQuote.value.quoteExpiration < new Date(),
  };
});

const doNotDispatchMessage = computed((): MessageSpecification => {
  return {
    id: 'do-not-dispatch-warning',
    textSlot: () => {
      const doNotDispatchEl = h('span', t('carriers.doNotDispatch'));
      if (canOverrideCarrierDoNotDispatch.value) {
        return doNotDispatchEl;
      }

      const errorEl = h(
        'span',
        t('rateShop.doNotDispatch.entityNameNoAssignmentErrorMessage', {
          entityName:
            rateQuote.value.source === 'BROKERAGE'
              ? lowerCase(t('brokerages.brokers'))
              : lowerCase(t('carriers.carriers')),
        }),
      );

      return h('div', { class: 'flex flex-column flex-gap-1' }, [
        doNotDispatchEl,
        errorEl,
      ]);
    },
    severity: canOverrideCarrierDoNotDispatch ? 'warn' : 'error',
    visible: isDoNotDispatchEnabled.value && rateQuote.value.doNotDispatch,
  };
});

const rateQuoteWarnings = computed((): MessageSpecification[] => {
  // an errored quote's warnings are the reason it could not rate, so surface them as errors
  const warningSeverity =
    rateQuote.value.rateStatus === 'ERROR' ? 'error' : 'warn';
  const warnings = rateQuote.value.warnings.map(
    (msg, index): MessageSpecification => ({
      id: `rate-quote-warning-${index}`,
      text: msg,
      severity: warningSeverity,
    }),
  );

  const carrierComplianceWarnings =
    rateQuote.value.carrierComplianceRuleWarnings.map(
      (msg, index): MessageSpecification => ({
        id: `carrier-compliance-warning-${index}`,
        text: msg,
        severity: 'warn',
      }),
    );

  return [...warnings, ...carrierComplianceWarnings];
});

const otherDetailsMessages = computed((): MessageSpecification[] => [
  expiredMessage.value,
  doNotDispatchMessage.value,
  ...rateQuoteWarnings.value,
]);

const collectRateQuoteCarrierDetails = (
  rateQuote: RateQuote,
): { k: string; v: string }[] => {
  const details: { k: string; v: string }[] = [];

  details.push({
    k: t('carriers.dotSafetyRating'),
    v: titleCase(rateQuote.carrier?.dotSafetyRating) ?? '-',
  });
  details.push({
    k: t('carriers.authorityStatusParam', {
      param: t('enumMap.DotAuthority.COMMON'),
    }),
    v: titleCase(rateQuote.carrier?.commonAuthority) ?? '-',
  });
  details.push({
    k: t('carriers.authorityStatusParam', {
      param: t('enumMap.DotAuthority.CONTRACT'),
    }),
    v: titleCase(rateQuote.carrier?.contractAuthority) ?? '-',
  });
  details.push({
    k: t('carriers.authorityStatusParam', {
      param: t('enumMap.DotAuthority.BROKER'),
    }),
    v: titleCase(rateQuote.carrier?.brokerAuthority) ?? '-',
  });
  return details;
};
</script>

<style>
.other-details-tab {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);

  .message-container {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .other-details-columns {
    display: flex;
    gap: var(--sp-3);

    > div {
      min-width: 0;
    }

    .quote-details {
      border-left: 1px solid var(--divider-color);
      padding-inline: var(--sp-3);
    }
  }
}
</style>
